import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken } from '../Services/services';
import { ROLES, hasRole } from '../utils/roles';
import { useApprovedAccess } from '../utils/useApprovedAccess';
import RestrictedAccess from './RestrictedAccess';
import {
  getMarketplaceCatalogItem,
  getMarketplaceSources,
  getResolvedPricing,
  patchCatalogItemQuantity,
  addToCart,
  getCart,
  removeCartItem,
  getSecondaryInventory,
} from '../api/inventory';
import { getInventoriesByProduct } from '../api/products';
import { track, logger } from '../utils/logger';
import AppHeader from './AppHeader';
import PermissionGate from './PermissionGate';
import { isInsufficientPermissionError } from '../utils/permissionErrors';
import InventoryDetailsModal, {
  CartIcon, ImgPlaceholderSvg, fmtPrice,
} from './InventoryDetailsModal';

// The marketplace catalog is availability-only; pricing is resolved live per
// condition (line → Price → VCP → CCP) and shown against each availability line.
const SOURCE_LABELS = {
  Inventory: 'On-Hand Inventory',
  'Secondary Inventory': 'Network Inventory',
};

// Only show "Tech Data" modal for on-hand inventory
const SUPPORTS_TECH_DATA = new Set(['Inventory']);

// Normalizes a condition name so resolver prices and availability lines match.
function condKey(v) {
  return String(v ?? '').trim().toUpperCase();
}

// ACON-65 (item 1): On-hand + network inventory are merged into a single view —
// the user doesn't need to know which record came from which source. VCP and
// Price List stay as their own separate tables (out of scope for this change).
const MERGED_INVENTORY_SOURCES = new Set(['Inventory', 'Secondary Inventory']);
const INVENTORY_PAGE_SIZE = 10;

function toNum(v) {
  // Number('') === 0 and Number(null) === 0 in JS, which would make an empty
  // "no filter set" range input silently behave like "value >= 0" and wrongly
  // exclude rows that have no numeric value at all (e.g. on-hand inventory rows
  // with no Price field). Treat blank/nullish input as "no value" (null).
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return mobile;
}

// ── QTY Picker modal ──────────────────────────────────────────────────────────
function QtyPickerModal({ line, partNumber, onConfirm, onCancel, primaryColor, existingCartItem }) {
  const maxQty = Number(line.Quantity ?? line.inscor__Quantity__c ?? 100);
  const isUpdate = !!existingCartItem;
  // New adds default to the full available quantity; updates keep the cart value.
  const [qty, setQty] = useState(isUpdate ? (existingCartItem.quantity_requested ?? 1) : maxQty);
  const condition = line.Condition || line.inscor__Condition__c || '';

  const clamp = (v) => Math.min(maxQty, Math.max(1, Number(v) || 1));

  return (
    <div style={qtyM.overlay}>
      <div style={qtyM.panel}>
        <div style={qtyM.header}>
          <div>
            <strong>{partNumber}</strong>
            {condition && <span style={{ marginLeft: 8, fontSize: 13, color: '#6b7280' }}>{condition}</span>}
          </div>
          <button style={qtyM.closeX} onClick={onCancel}>✕</button>
        </div>
        <div style={{ padding: '20px 28px 24px' }}>
          {isUpdate && (
            <div style={qtyM.dupWarning}>
              <svg viewBox="0 0 24 24" fill="none" width={16} height={16}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              This inventory line is already in your cart
              {existingCartItem.quantity_requested != null && (
                <span style={qtyM.dupCurrent}>
                  Current qty: <strong>{existingCartItem.quantity_requested}</strong>
                </span>
              )}
            </div>
          )}
          <p style={qtyM.prompt}>
            {isUpdate ? 'Update the quantity in your cart:' : 'How many would you like to add?'}
          </p>
          <p style={qtyM.avail}>Up to <strong>{maxQty}</strong> available</p>
          <div style={qtyM.stepper}>
            <button style={qtyM.stepBtn} onClick={() => setQty((v) => clamp(v - 1))}>−</button>
            <input
              type="number" min={1} max={maxQty} value={qty}
              onChange={(e) => setQty(clamp(e.target.value))}
              style={qtyM.input}
              aria-label="Quantity"
            />
            <button style={qtyM.stepBtn} onClick={() => setQty((v) => clamp(v + 1))}>+</button>
          </div>
        </div>
        <div style={qtyM.footer}>
          <button
            style={{ ...qtyM.addBtn, backgroundColor: primaryColor }}
            onClick={() => onConfirm(qty)}
          >
            {isUpdate ? (
              `Update Cart — qty ${qty}`
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                + <CartIcon size={14} /> Add {qty} to Cart
              </span>
            )}
          </button>
          <button style={qtyM.cancelBtn} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const qtyM = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1100, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { backgroundColor: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontSize: 15, fontWeight: 700 },
  closeX: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280', padding: '2px 6px' },
  dupWarning: {
    display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap',
    backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
    borderRadius: 8, padding: '10px 14px', marginBottom: 16,
    fontSize: 13, fontWeight: 600, color: '#9a3412',
  },
  dupCurrent: { marginLeft: 'auto', fontWeight: 400, color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' },
  prompt: { margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: '#111827' },
  avail: { margin: '0 0 20px', fontSize: 13, color: '#6b7280' },
  stepper: { display: 'flex', alignItems: 'center', gap: 10 },
  stepBtn: { width: 36, height: 36, borderRadius: 8, border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  input: { width: 80, height: 36, borderRadius: 8, border: '1px solid #d1d5db', textAlign: 'center', fontSize: 16, fontWeight: 600 },
  footer: { display: 'flex', gap: 10, padding: '14px 20px', backgroundColor: '#fafafa', borderTop: '1px solid #e5e7eb' },
  addBtn: { color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: 1 },
  cancelBtn: { backgroundColor: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, padding: '9px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};

// ── Main component ────────────────────────────────────────────────────────────
export default function InventoryItemDetail() {
  const { itemId: itemIdParam } = useParams();
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  // Product detail is approved-only. Guests/pending may search + view results
  // but cannot open this page (ACON access limit).
  const { checked: accessChecked, allowed: accessAllowed, isGuest, role: userRole } = useApprovedAccess();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);

  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);
  const [inventoryPermissionBlocked, setInventoryPermissionBlocked] = useState(false);
  const [inventoryData, setInventoryData] = useState(null);
  const [quantitySynced, setQuantitySynced] = useState(false);

  const [cartBusy, setCartBusy] = useState({});
  const [toast, setToast] = useState(null);

  // Tech Data modal
  const [techDataLine, setTechDataLine] = useState(null);
  // QTY picker — also carries existingCartItem when the line is already in the cart
  const [qtyPickerLine, setQtyPickerLine] = useState(null);
  const [qtyPickerSource, setQtyPickerSource] = useState('');
  const [qtyPickerExisting, setQtyPickerExisting] = useState(null); // { id, quantity_requested }

  // Live cart items for duplicate detection
  const [cartItems, setCartItems] = useState([]);

  // ACON-65 (items 2a-2g): filters/sort/pagination for the merged inventory table.
  const [invFiltersOpen, setInvFiltersOpen] = useState(false);
  const [invConditionFilter, setInvConditionFilter] = useState([]); // array of selected condition codes
  const [invPriceMin, setInvPriceMin] = useState('');
  const [invPriceMax, setInvPriceMax] = useState('');
  const [invQtyMin, setInvQtyMin] = useState('');
  const [invQtyMax, setInvQtyMax] = useState('');
  const [invPricedOnly, setInvPricedOnly] = useState(false);
  const [invSort, setInvSort] = useState(''); // e.g. "price_desc"
  const [invPage, setInvPage] = useState(1);

  const itemId = itemIdParam ? decodeURIComponent(itemIdParam) : '';
  const primaryColor = branding.primaryColor;

  useEffect(() => {
    // Don't fetch detail data until the user is confirmed as approved — this
    // avoids needless requests for guests/pending who are blocked below.
    if (!accessAllowed) return;
    let cancelled = false;
    const run = async () => {
      if (!itemId) { setError('Missing item'); setLoading(false); return; }
      setLoading(true); setError('');
      try {
        const data = await getMarketplaceCatalogItem(itemId);
        if (!cancelled) setItem(data || null);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load item');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [itemId, accessAllowed]);

  const loadInventory = useCallback(async (currentItem) => {
    if (!currentItem?.sf_product_id) return;
    setInventoryLoading(true); setInventoryError(null); setInventoryData(null); setQuantitySynced(false);
    try {
      const [sourcesResp] = await Promise.allSettled([getMarketplaceSources()]);
      const activeSources = sourcesResp.status === 'fulfilled' ? (sourcesResp.value?.sources || []) : ['Inventory'];
      const pid = currentItem.sf_product_id;

      // Availability sources only — these are the lines a buyer can add to cart.
      // Pricing (Price/VCP/CCP) is not shown as its own section; it feeds the
      // single resolved price per condition returned by the pricing resolver.
      const fetchers = {};
      if (activeSources.includes('Inventory')) fetchers.Inventory = getInventoriesByProduct(pid);
      if (activeSources.includes('Secondary Inventory')) fetchers['Secondary Inventory'] = getSecondaryInventory(pid);

      const keys = Object.keys(fetchers);
      const [pricingResp, ...results] = await Promise.allSettled([
        getResolvedPricing(pid),
        ...Object.values(fetchers),
      ]);

      // condition → { price, source } resolved live (line → Price → VCP → CCP).
      const priceByCondition = {};
      let currency = 'USD';
      if (pricingResp.status === 'fulfilled' && pricingResp.value) {
        currency = pricingResp.value.currency || 'USD';
        for (const p of pricingResp.value.prices || []) {
          priceByCondition[condKey(p.condition)] = { price: p.price, source: p.source };
        }
      }

      const grouped = {};
      let totalLiveQty = 0;

      keys.forEach((source, i) => {
        const res = results[i];
        if (res.status === 'fulfilled') {
          const lines = Array.isArray(res.value) ? res.value : (res.value?.items || res.value?.records || []);
          lines.forEach((line) => {
            const qty = Number(line.inscor__Quantity__c ?? line.Quantity ?? line.quantity ?? 0);
            totalLiveQty += qty;
            const resolved = priceByCondition[condKey(line.Condition || line.inscor__Condition__c)];
            line.__resolvedPrice = resolved ? resolved.price : null;
            line.__resolvedSource = resolved ? resolved.source : null;
            line.__currency = currency;
          });
          grouped[source] = lines;
        } else { grouped[source] = []; }
      });

      setInventoryData({ grouped, activeSources: keys, totalLiveQty, priceByCondition, currency });

      const cosmosQty = Number(currentItem.quantity ?? 0);
      if (totalLiveQty !== cosmosQty && currentItem.id) {
        try { await patchCatalogItemQuantity(currentItem.id, totalLiveQty); setItem((prev) => ({ ...prev, quantity: totalLiveQty })); setQuantitySynced(true); }
        catch { /* non-critical */ }
      }
    } catch (e) {
      setInventoryError(e);
    } finally { setInventoryLoading(false); }
  }, []);

  // Keep cartItems fresh for duplicate detection
  const refreshCartItems = useCallback(() => {
    if (!getSessionToken()) { setCartItems([]); return; }
    getCart().then(d => setCartItems(d?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    refreshCartItems();
    window.addEventListener('cartUpdated', refreshCartItems);
    return () => window.removeEventListener('cartUpdated', refreshCartItems);
  }, [refreshCartItems]);

  useEffect(() => { if (item) loadInventory(item); }, [item, loadInventory]);

  // ACON-65 (item 1): merge on-hand ("Inventory") + network ("Secondary Inventory")
  // rows into one list. Each row keeps its real `_sourceType` internally (needed for
  // Add to Cart / Tech Data) even though it's never shown to the user as a column.
  const mergedInventoryRows = useMemo(() => {
    if (!inventoryData) return [];
    const rows = [];
    for (const source of MERGED_INVENTORY_SOURCES) {
      const lines = inventoryData.grouped[source] || [];
      lines.forEach((line) => rows.push({ ...line, _sourceType: source }));
    }
    return rows;
  }, [inventoryData]);

  const invConditionOptions = useMemo(() => {
    const set = new Set();
    mergedInventoryRows.forEach((l) => {
      const c = l.Condition || l.inscor__Condition__c;
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [mergedInventoryRows]);

  const filteredSortedInventoryRows = useMemo(() => {
    let rows = mergedInventoryRows;

    if (invConditionFilter.length > 0) {
      rows = rows.filter((l) => invConditionFilter.includes(l.Condition || l.inscor__Condition__c));
    }
    const priceMin = toNum(invPriceMin);
    const priceMax = toNum(invPriceMax);
    const qtyMin = toNum(invQtyMin);
    const qtyMax = toNum(invQtyMax);
    if (invPricedOnly) {
      rows = rows.filter((l) => toNum(l.__resolvedPrice ?? l.Price ?? l.inscor__Price__c) != null);
    }
    if (priceMin != null) {
      rows = rows.filter((l) => { const p = toNum(l.__resolvedPrice ?? l.Price ?? l.inscor__Price__c); return p != null && p >= priceMin; });
    }
    if (priceMax != null) {
      rows = rows.filter((l) => { const p = toNum(l.__resolvedPrice ?? l.Price ?? l.inscor__Price__c); return p != null && p <= priceMax; });
    }
    if (qtyMin != null) {
      rows = rows.filter((l) => { const q = toNum(l.Quantity ?? l.inscor__Quantity__c); return q != null && q >= qtyMin; });
    }
    if (qtyMax != null) {
      rows = rows.filter((l) => { const q = toNum(l.Quantity ?? l.inscor__Quantity__c); return q != null && q <= qtyMax; });
    }

    if (invSort) {
      const lastUnderscore = invSort.lastIndexOf('_');
      const sortKey = lastUnderscore >= 0 ? invSort.slice(0, lastUnderscore) : invSort;
      const sortDir = lastUnderscore >= 0 ? invSort.slice(lastUnderscore + 1) : 'asc';
      const dirMul = sortDir === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        let av; let bv;
        if (sortKey === 'condition') {
          av = (a.Condition || a.inscor__Condition__c || '').toLowerCase();
          bv = (b.Condition || b.inscor__Condition__c || '').toLowerCase();
        } else if (sortKey === 'price') {
          av = toNum(a.__resolvedPrice ?? a.Price ?? a.inscor__Price__c) ?? -Infinity;
          bv = toNum(b.__resolvedPrice ?? b.Price ?? b.inscor__Price__c) ?? -Infinity;
        } else if (sortKey === 'qty') {
          av = toNum(a.Quantity ?? a.inscor__Quantity__c) ?? -Infinity;
          bv = toNum(b.Quantity ?? b.inscor__Quantity__c) ?? -Infinity;
        } else {
          return 0;
        }
        if (av < bv) return -1 * dirMul;
        if (av > bv) return 1 * dirMul;
        return 0;
      });
    }

    return rows;
  }, [mergedInventoryRows, invConditionFilter, invPriceMin, invPriceMax, invQtyMin, invQtyMax, invPricedOnly, invSort]);

  const invTotalPages = Math.max(1, Math.ceil(filteredSortedInventoryRows.length / INVENTORY_PAGE_SIZE));
  useEffect(() => { if (invPage > invTotalPages) setInvPage(invTotalPages); }, [invPage, invTotalPages]);
  useEffect(() => { setInvPage(1); }, [invConditionFilter, invPriceMin, invPriceMax, invQtyMin, invQtyMax, invPricedOnly]);

  const paginatedInventoryRows = useMemo(() => {
    const start = (invPage - 1) * INVENTORY_PAGE_SIZE;
    return filteredSortedInventoryRows.slice(start, start + INVENTORY_PAGE_SIZE);
  }, [filteredSortedInventoryRows, invPage]);

  const toggleConditionFilter = (cond) => {
    setInvConditionFilter((prev) => (
      prev.includes(cond) ? prev.filter((c) => c !== cond) : [...prev, cond]
    ));
  };

  const invActiveFilterCount = [
    invConditionFilter.length > 0,
    invPriceMin !== '' || invPriceMax !== '',
    invQtyMin !== '' || invQtyMax !== '',
    invPricedOnly,
  ].filter(Boolean).length;

  const handleInvSort = (key) => {
    const [curKey, curDir] = invSort ? [invSort.slice(0, invSort.lastIndexOf('_')), invSort.slice(invSort.lastIndexOf('_') + 1)] : [null, null];
    const nextDir = curKey === key && curDir === 'asc' ? 'desc' : 'asc';
    setInvSort(`${key}_${nextDir}`);
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const doAddToCart = useCallback(async (line, sourceType, qty = 1, existingCartItemId = null) => {
    if (!getSessionToken()) { openLogin({ redirectPath: `/inventory/item/${encodeURIComponent(itemId)}` }); return; }
    if (!hasRole(userRole, ROLES.PENDING)) {
      logger.warn('cart.add_blocked', { reason: 'role_insufficient', role: userRole, sourceType });
      openLogin({ redirectPath: `/inventory/item/${encodeURIComponent(itemId)}` });
      return;
    }
    const lineKey = line.Id || line.id || `${sourceType}-${Math.random()}`;
    setCartBusy((prev) => ({ ...prev, [lineKey]: true }));
    try {
      // Remove existing cart item before re-adding with updated qty
      if (existingCartItemId) await removeCartItem(existingCartItemId);
      const added = await addToCart({
        sf_product_id: item?.sf_product_id || null,
        sf_inventory_line_id: line.Id || null,
        part_number: item?.part_number || line.inscor__Part_Number__c || '',
        condition: line.Condition || line.inscor__Condition__c || item?.condition || '',
        description: item?.description || '',
        source_type: sourceType,
        quantity_requested: qty,
        quantity_available: Number(line.inscor__Quantity__c ?? line.Quantity ?? line.quantity ?? 0) || null,
        // Use the live resolved price (line → Price → VCP → CCP) for this condition,
        // falling back to the availability line's own price if none resolved.
        price: Number(line.__resolvedPrice ?? line.inscor__Price__c ?? line.Price ?? line.price ?? 0) || null,
        uom: item?.uom || 'EA',
        warehouse_name: line.WarehouseName || line.inscor__Warehouse_Name__c || null,
      });
      track('cart.add', { kind: 'part', sourceType, qty, isUpdate: !!existingCartItemId, section: added?.cart_section });
      const sectionMsg = added?.cart_section === 'rfq'
        ? 'Added to Request a Quote (missing purchase data)'
        : (existingCartItemId ? `Cart updated — qty is now ${qty}` : `Added ${qty} to Shopping Cart`);
      setToast({ type: 'success', message: sectionMsg });
      window.dispatchEvent(new Event('cartUpdated'));
      if (techDataLine) setTechDataLine(null);
    } catch (e) {
      logger.error('cart.add_failed', { kind: 'part', sourceType, qty, status: e?.status, message: e?.message });
      if (isInsufficientPermissionError(e)) {
        setInventoryPermissionBlocked(true);
      } else {
        setToast({ type: 'error', message: e?.message || 'Failed to add to cart' });
      }
    } finally {
      setCartBusy((prev) => ({ ...prev, [lineKey]: false }));
    }
  }, [item, itemId, userRole, openLogin, techDataLine]);

  // Cart button handler — detects duplicates, shows qty picker
  const handleCartClick = useCallback((line, sourceType) => {
    const sfLineId = line.Id || line.id;
    const existing = sfLineId
      ? cartItems.find(ci => ci.sf_inventory_line_id === sfLineId)
      : null;

    const availQty = Number(line.Quantity ?? line.inscor__Quantity__c ?? 1);

    if (existing || availQty > 1) {
      setQtyPickerLine(line);
      setQtyPickerSource(sourceType);
      setQtyPickerExisting(existing || null);
    } else {
      doAddToCart(line, sourceType, 1);
    }
  }, [doAddToCart, cartItems]);

  const handleQtyConfirm = (qty) => {
    if (qtyPickerLine) {
      doAddToCart(qtyPickerLine, qtyPickerSource, qty, qtyPickerExisting?.id ?? null);
    }
    setQtyPickerLine(null);
    setQtyPickerExisting(null);
  };

  const handleQtyCancel = () => {
    setQtyPickerLine(null);
    setQtyPickerExisting(null);
  };

  // ACON-65: merged on-hand + network table — filters, sortable headers, pagination.
  // Rows carry `_sourceType` internally (on-hand vs network) so Add to Cart / Tech
  // Data keep working correctly even though the origin isn't shown to the user.
  const renderMergedInventoryTable = () => {
    const sortDirFor = (key) => {
      if (!invSort) return null;
      const idx = invSort.lastIndexOf('_');
      const curKey = idx >= 0 ? invSort.slice(0, idx) : invSort;
      return curKey === key ? invSort.slice(idx + 1) : null;
    };
    const sortHeader = (key, label) => {
      const dir = sortDirFor(key);
      return (
        <th
          key={key}
          style={{ ...s.invTh, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleInvSort(key)}
          role="button"
          aria-label={`Sort by ${label}`}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {label}
            <span style={{ opacity: dir ? 1 : 0.3, fontSize: 10 }}>{dir === 'desc' ? '▼' : '▲'}</span>
          </span>
        </th>
      );
    };

    return (
      <div style={s.sourceBlock}>
        <div style={s.sourceHeader}>
          <h2 style={s.sourceTitle}>Inventory</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={s.sourceCount}>
              {filteredSortedInventoryRows.length} record{filteredSortedInventoryRows.length !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              style={invFiltersOpen ? s.filtersToggleBtnActive : s.filtersToggleBtn}
              onClick={() => setInvFiltersOpen((v) => !v)}
              aria-expanded={invFiltersOpen}
            >
              Filters{invActiveFilterCount > 0 ? ` (${invActiveFilterCount})` : ''}
            </button>
          </div>
        </div>

        {invFiltersOpen && (
          <div style={s.filterPanel}>
            <div style={s.filterField}>
              <label style={s.filterLabel}>Condition</label>
              <div style={s.conditionChecks}>
                {invConditionOptions.length === 0 && <span style={s.muted}>No conditions</span>}
                {invConditionOptions.map((c) => (
                  <label key={c} style={s.conditionCheckLabel}>
                    <input
                      type="checkbox"
                      checked={invConditionFilter.includes(c)}
                      onChange={() => toggleConditionFilter(c)}
                    />
                    {c}
                  </label>
                ))}
              </div>
            </div>
            <div style={s.filterField}>
              <label style={s.filterLabel}>Price range</label>
              <div style={s.rangeRow}>
                <input type="number" placeholder="Min" value={invPriceMin} onChange={(e) => setInvPriceMin(e.target.value)} style={s.rangeInput} />
                <span>–</span>
                <input type="number" placeholder="Max" value={invPriceMax} onChange={(e) => setInvPriceMax(e.target.value)} style={s.rangeInput} />
              </div>
            </div>
            <div style={s.filterField}>
              <label style={s.filterLabel}>Quantity range</label>
              <div style={s.rangeRow}>
                <input type="number" placeholder="Min" value={invQtyMin} onChange={(e) => setInvQtyMin(e.target.value)} style={s.rangeInput} />
                <span>–</span>
                <input type="number" placeholder="Max" value={invQtyMax} onChange={(e) => setInvQtyMax(e.target.value)} style={s.rangeInput} />
              </div>
            </div>
            <label style={s.toggleLabel}>
              <input type="checkbox" checked={invPricedOnly} onChange={(e) => setInvPricedOnly(e.target.checked)} />
              Priced inventory only
            </label>
          </div>
        )}

        {filteredSortedInventoryRows.length === 0 ? (
          <p style={s.muted}>No inventory records match the selected filters.</p>
        ) : (
          <>
            <div style={s.tableWrap}>
              <table style={s.invTable}>
                <thead>
                  <tr>
                    {sortHeader('condition', 'Condition')}
                    {sortHeader('qty', 'Qty')}
                    {sortHeader('price', 'Price')}
                    <th style={s.invTh}>UOM</th>
                    <th style={s.invTh}>Serial #</th>
                    <th style={{ ...s.invTh, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInventoryRows.map((line, idx) => {
                    const lineKey = line.Id || `${line._sourceType}-${idx}`;
                    const busy = cartBusy[lineKey];
                    const showTechData = SUPPORTS_TECH_DATA.has(line._sourceType);
                    return (
                      <tr key={lineKey} style={idx % 2 === 0 ? s.invRowEven : undefined}>
                        <td style={s.invTd}>{line.Condition || line.inscor__Condition__c || '—'}</td>
                        <td style={s.invTd}>{line.Quantity ?? line.inscor__Quantity__c ?? '—'}</td>
                        <td style={s.invTd}>{fmtPrice(line.__resolvedPrice ?? line.Price ?? line.inscor__Price__c)}</td>
                        <td style={s.invTd}>{line.UOM || line.inscor__Unit_of_Measure__c || '—'}</td>
                        <td style={s.invTd}>{line.SerialNumber || line.inscor__Serial_Number__c || '—'}</td>
                        <td style={{ ...s.invTd, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              style={{ ...s.actionBtn, backgroundColor: primaryColor, ...(busy ? s.btnBusy : {}) }}
                              disabled={busy}
                              onClick={() => handleCartClick(line, line._sourceType)}
                              title="Add to cart"
                            >
                              {busy ? '…' : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  + <CartIcon size={12} />
                                </span>
                              )}
                            </button>
                            {showTechData && (
                              <button style={s.techDataBtn} onClick={() => setTechDataLine(line)}>
                                Tech Data
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={s.pager}>
              <button type="button" style={invPage <= 1 ? s.pagerBtnDisabled : s.pagerBtn} disabled={invPage <= 1} onClick={() => setInvPage((p) => p - 1)}>Previous</button>
              <span style={s.pagerInfo}>Page {invPage} of {invTotalPages} ({filteredSortedInventoryRows.length} record{filteredSortedInventoryRows.length !== 1 ? 's' : ''})</span>
              <button type="button" style={invPage >= invTotalPages ? s.pagerBtnDisabled : s.pagerBtn} disabled={invPage >= invTotalPages} onClick={() => setInvPage((p) => p + 1)}>Next</button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTable = (sourceType, lines) => {
    if (!lines?.length) return null;
    const columns = getColumnsForSource(sourceType);
    const showTechData = SUPPORTS_TECH_DATA.has(sourceType);
    return (
      <div style={s.tableWrap}>
        <table style={s.invTable}>
          <thead>
            <tr>
              {columns.map((col) => <th key={col.key} style={s.invTh}>{col.label}</th>)}
              <th style={{ ...s.invTh, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, idx) => {
              const lineKey = line.Id || `${sourceType}-${idx}`;
              const busy = cartBusy[lineKey];
              return (
                <tr key={lineKey} style={idx % 2 === 0 ? s.invRowEven : undefined}>
                  {columns.map((col) => (
                    <td key={col.key} style={s.invTd}>
                      {col.format ? col.format(col.resolve(line)) : (col.resolve(line) ?? '—')}
                    </td>
                  ))}
                  <td style={{ ...s.invTd, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        style={{ ...s.actionBtn, backgroundColor: primaryColor, ...(busy ? s.btnBusy : {}) }}
                        disabled={busy}
                        onClick={() => handleCartClick(line, sourceType)}
                        title="Add to cart"
                      >
                        {busy ? '…' : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            + <CartIcon size={12} />
                          </span>
                        )}
                      </button>
                      {showTechData && (
                        <button style={s.techDataBtn} onClick={() => setTechDataLine(line)}>
                          Tech Data
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const totalQty = inventoryData?.totalLiveQty ?? item?.quantity;
  const inventoryPermissionGate =
    inventoryPermissionBlocked || isInsufficientPermissionError(inventoryError);

  // Block guests and pending users from the detail page entirely.
  if (accessChecked && !accessAllowed) {
    return (
      <RestrictedAccess
        isGuest={isGuest}
        redirectPath={`/inventory/item/${encodeURIComponent(itemId)}`}
      />
    );
  }

  return (
    <div style={s.page}>
      <AppHeader />

      {toast && (
        <div style={{ ...s.toast, backgroundColor: toast.type === 'success' ? '#059669' : '#dc2626' }}>
          {toast.message}
        </div>
      )}

      {techDataLine && (
        <InventoryDetailsModal
          line={techDataLine}
          partNumber={item?.part_number || ''}
          productId={item?.sf_product_id || null}
          onClose={() => setTechDataLine(null)}
          onAddToCart={(line) => handleCartClick(line, 'Inventory')}
          primaryColor={primaryColor}
          cartBusy={cartBusy}
          userRole={userRole}
        />
      )}

      {qtyPickerLine && (
        <QtyPickerModal
          line={qtyPickerLine}
          partNumber={item?.part_number || ''}
          onConfirm={handleQtyConfirm}
          onCancel={handleQtyCancel}
          primaryColor={primaryColor}
          existingCartItem={qtyPickerExisting}
        />
      )}

      <main style={s.main}>
        {loading && <p style={s.muted}>Loading…</p>}
        {error && <p style={s.err}>{error}</p>}

        {!loading && !error && item && (
          <>
            {/* Top bar */}
            <div style={s.topBar}>
              <div style={s.topImgWrap}><ImgPlaceholderSvg size={30} /></div>
              <div style={s.topInfo}>
                <span style={s.topPartNum}>{item.part_number}</span>
                {(item.keyword || item.description) && (
                  <span style={s.topKeyword}>{item.keyword || item.description?.split(' ').slice(0, 4).join(' ')}</span>
                )}
              </div>
              <div style={s.topQtyWrap}>
                {totalQty != null ? (
                  <span style={{ ...s.topQty, borderColor: `${primaryColor}40`, color: primaryColor, backgroundColor: `${primaryColor}0f` }}>
                    {totalQty} available
                  </span>
                ) : inventoryLoading ? (
                  <span style={s.topQtyLoading}>Loading availability…</span>
                ) : null}
                {quantitySynced && <span style={s.syncDot} title="Quantity updated from live data">↻</span>}
                <Link to="/catalog" style={s.topCatalogLink}>← Catalog</Link>
              </div>
            </div>

            {inventoryLoading && (
              <div style={s.loadingRow}>
                <div style={{ ...s.spinner, borderTopColor: primaryColor }} />
                <span style={s.muted}>Loading live inventory…</span>
              </div>
            )}
            {inventoryError && !inventoryPermissionGate && !inventoryLoading && (
              <div style={s.errCard}>
                <strong>Could not load live inventory</strong>
                <p style={{ margin: '4px 0 0', fontSize: 13 }}>
                  {inventoryError.message || 'Failed to load live inventory'}
                </p>
              </div>
            )}
            {!item.sf_product_id && !inventoryLoading && (
              <div style={s.noSfCard}>No Salesforce product linked — live availability is not available for this item.</div>
            )}

            <PermissionGate
              blocked={inventoryPermissionGate && !inventoryLoading}
              primaryColor={primaryColor}
              minHeight={360}
            >
              {inventoryData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {mergedInventoryRows.length > 0 && renderMergedInventoryTable()}
                  {inventoryData.activeSources.map((source) => {
                    if (MERGED_INVENTORY_SOURCES.has(source)) return null; // rendered above, merged
                    const lines = inventoryData.grouped[source] || [];
                    if (lines.length === 0) return null;
                    return (
                      <div key={source} style={s.sourceBlock}>
                        <div style={s.sourceHeader}>
                          <h2 style={s.sourceTitle}>{SOURCE_LABELS[source] || source}</h2>
                          <span style={s.sourceCount}>{lines.length} record{lines.length !== 1 ? 's' : ''}</span>
                        </div>
                        {renderTable(source, lines)}
                      </div>
                    );
                  })}
                </div>
              )}
            </PermissionGate>
          </>
        )}
      </main>
    </div>
  );
}

// NOTE: 'Inventory' (on-hand) and 'Secondary Inventory' (network) are no longer
// rendered through this generic table — they're merged into one filterable/sortable/
// paginated table (see renderMergedInventoryTable) per ACON-65 item 1. This function
// now only backs the still-separate VCP and Price List tables.
function getColumnsForSource(sourceType) {
  switch (sourceType) {
    case 'VCP':
      return [
        { key: 'vendor', label: 'Vendor', resolve: (l) => l.VendorName || l.inscor__Vendor_Name__c },
        { key: 'condition', label: 'Condition', resolve: (l) => l.Condition || l.inscor__Condition__c },
        { key: 'price', label: 'Price', resolve: (l) => l.Price ?? l.inscor__Price__c, format: fmtPrice },
        { key: 'leadTime', label: 'Lead Time', resolve: (l) => l.LeadTime || l.inscor__Lead_Time__c },
        { key: 'minQty', label: 'Min Qty', resolve: (l) => l.MinQuantity ?? l.inscor__Minimum_Quantity__c },
        { key: 'effective', label: 'Effective', resolve: (l) => l.EffectiveDate || l.inscor__Effective_Date__c },
      ];
    case 'Price':
      return [
        { key: 'condition', label: 'Condition', resolve: (l) => l.Condition || l.inscor__Condition_Code__c?.Name || l.inscor__Condition__c },
        { key: 'price', label: 'Price', resolve: (l) => l.Price ?? l.inscor__Price__c, format: fmtPrice },
        { key: 'quantity', label: 'Qty', resolve: (l) => l.Quantity ?? l.inscor__Quantity__c },
        { key: 'currency', label: 'Currency', resolve: (l) => l.CurrencyIsoCode || 'USD' },
      ];
    default:
      return [
        { key: 'condition', label: 'Condition', resolve: (l) => l.Condition || l.inscor__Condition__c },
        { key: 'qty', label: 'Qty', resolve: (l) => l.Quantity ?? l.inscor__Quantity__c },
        { key: 'price', label: 'Price', resolve: (l) => l.__resolvedPrice ?? l.Price ?? l.inscor__Price__c, format: fmtPrice },
      ];
  }
}

const s = {
  page:  { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  main:  { padding: '20px 24px', maxWidth: 1280, margin: '0 auto' },
  muted: { color: 'var(--muted-foreground)', fontSize: 14 },
  err:   { color: 'var(--destructive)', fontSize: 14 },
  topBar: { display: 'flex', alignItems: 'center', gap: 14, backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 18px', marginBottom: 20 },
  topImgWrap: { width: 52, height: 52, flexShrink: 0, borderRadius: 8, backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  topInfo: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  topPartNum: { fontSize: 18, fontWeight: 800, letterSpacing: '-0.3px' },
  topKeyword: { fontSize: 13, color: 'var(--muted-foreground)' },
  topQtyWrap: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  topCatalogLink: { marginLeft: 12, fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none', whiteSpace: 'nowrap', backgroundColor: '#ffffff', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 16px', display: 'inline-block' },
  topQty: { fontSize: 13, fontWeight: 700, border: '1px solid', borderRadius: 20, padding: '4px 12px' },
  topQtyLoading: { fontSize: 13, color: 'var(--muted-foreground)' },
  syncDot: { fontSize: 14, color: '#059669' },
  loadingRow: { display: 'flex', alignItems: 'center', gap: 12, backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', marginBottom: 12 },
  spinner: { width: 18, height: 18, border: '2px solid #e5e7eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  errCard: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '14px 18px', color: '#dc2626', fontSize: 14, marginBottom: 12 },
  noSfCard: { backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', color: 'var(--muted-foreground)', fontSize: 14, fontStyle: 'italic' },
  sourceBlock: { backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px' },
  sourceHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  sourceTitle: { margin: 0, fontSize: 15, fontWeight: 700 },
  sourceCount: { fontSize: 12, color: 'var(--muted-foreground)' },
  tableWrap: { overflowX: 'auto', marginTop: 10 },
  invTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  invTh: { textAlign: 'left', padding: '7px 10px', backgroundColor: 'var(--muted)', fontWeight: 600, fontSize: 12, borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' },
  invTd: { padding: '8px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  invRowEven: { backgroundColor: 'var(--secondary)' },
  actionBtn: { color: 'white', border: 'none', borderRadius: 6, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  techDataBtn: { backgroundColor: 'transparent', border: '1px solid #d1d5db', color: '#374151', borderRadius: 6, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnBusy: { opacity: 0.6, cursor: 'not-allowed' },
  toast: { position: 'fixed', top: 72, right: 24, color: 'white', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 500, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },

  // ACON-65: collapsible filters + pagination for the merged inventory table.
  filtersToggleBtn: { display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--foreground)', borderRadius: 8, padding: '6px 12px', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  filtersToggleBtnActive: { display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--primary)', background: 'var(--secondary)', color: 'var(--foreground)', borderRadius: 8, padding: '6px 12px', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  filterPanel: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 20, backgroundColor: 'var(--secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', margin: '10px 0' },
  filterField: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  conditionChecks: { display: 'flex', flexWrap: 'wrap', gap: 10, maxWidth: 320 },
  conditionCheckLabel: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--foreground)', whiteSpace: 'nowrap' },
  rangeRow: { display: 'flex', alignItems: 'center', gap: 6 },
  rangeInput: { width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, backgroundColor: 'var(--card)', color: 'var(--foreground)' },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--foreground)', whiteSpace: 'nowrap', cursor: 'pointer', alignSelf: 'center' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 12 },
  pagerBtn: { border: '1px solid #d1d5db', background: 'transparent', color: '#374151', borderRadius: 8, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  pagerBtnDisabled: { border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#9ca3af', borderRadius: 8, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'not-allowed' },
  pagerInfo: { fontSize: 13, color: 'var(--muted-foreground)', fontWeight: 500 },
};
